import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Link } from "react-router-dom";
import { Star, ArrowLeft, MessageSquare } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import AddReviewDialog from "@/components/site/AddReviewDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ count: 0, average: 0 });
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const initialReview = useMemo(() => {
    const rate = parseInt(params.get("rate") || "0", 10);
    if (!rate && !params.get("review")) return null;
    return { rating: Math.min(5, Math.max(0, rate)), name: params.get("name") || "", ref_id: params.get("ref") || "" };
  }, [params]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/reviews`, { params: { _: Date.now() } }),
        axios.get(`${API}/reviews/summary`, { params: { _: Date.now() } }),
      ]);
      setReviews(r.data.items || []);
      setSummary(s.data || { count: 0, average: 0 });
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const onChange = () => load();
    window.addEventListener("hp:reviews-changed", onChange);
    return () => window.removeEventListener("hp:reviews-changed", onChange);
  }, [load]);

  const filled = Math.round(summary.average);

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="reviews-page">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        <Link to="/" data-testid="reviews-back-home" className="inline-flex items-center gap-1 text-sm font-bold text-[#0C2E2B] hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back home
        </Link>

        <div className="mt-6 text-center">
          <div className="font-tactical text-[11px] uppercase tracking-[0.25em] text-[#0C2E2B]">Customer Reviews</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl text-[#0C2E2B] mt-2">
            {summary.count > 0 ? `${summary.average.toFixed(1)} out of 5` : "Be the first to review"}
          </h1>
          <div className="mt-3 flex justify-center">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className={`w-7 h-7 ${i <= filled ? "fill-[#FFDF00] text-[#FFDF00]" : "text-zinc-300"}`} />
            ))}
          </div>
          <p className="text-sm text-zinc-500 mt-2" data-testid="reviews-count">
            Based on {summary.count} {summary.count === 1 ? "review" : "reviews"}
          </p>
          <AddReviewDialog
            initial={initialReview}
            trigger={
              <Button data-testid="reviews-page-add-btn" className="btn-kinetic rounded-md mt-6 px-6 py-5 text-sm">
                Leave a Review
              </Button>
            }
            onSubmitted={load}
          />
        </div>

        <div className="mt-12 space-y-4">
          {loading ? (
            <div className="text-center text-sm text-zinc-500">Loading...</div>
          ) : reviews.length === 0 ? (
            <Card className="p-10 text-center">
              <MessageSquare className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
              <p className="font-bold text-zinc-800">No reviews yet</p>
              <p className="text-xs text-zinc-500 mt-1">Yours will be the first — the crew will appreciate it.</p>
            </Card>
          ) : (
            reviews.map((r) => (
              <Card key={r.id} data-testid={`review-card-${r.id}`} className="p-6 border-2 border-zinc-200 card-lift">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display font-black text-[#0C2E2B]">{r.name}</div>
                    <div className="flex mt-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className={`w-4 h-4 ${i <= r.rating ? "fill-[#FFDF00] text-[#FFDF00]" : "text-zinc-300"}`} />
                      ))}
                    </div>
                  </div>
                  <div className="text-[10px] text-zinc-400 whitespace-nowrap">
                    {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                </div>
                {r.comment && <p className="mt-3 text-sm text-zinc-700 leading-relaxed">{r.comment}</p>}
              </Card>
            ))
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
